/**
 * "This part of the app is not on this device, and there is no connection to
 * fetch it" — said in those words rather than as a stack trace.
 *
 * WHAT IT REPLACES. A page whose chunk could not load hit the generic
 * ErrorBoundary, which told the person: "Something went wrong. An unexpected
 * error occurred. Please try again or contact support if the problem
 * persists." — followed by the literal string "Failed to fetch dynamically
 * imported module: https://…". Nothing in that names the connection, and
 * "contact support" is the wrong instruction for a wifi outage.
 *
 * It is deliberately not a spinner. A spinner says "wait and this will
 * finish", and it will not: the code is not on the device and cannot be
 * fetched. Saying so lets somebody act — move to where there is signal, use
 * the front desk, or just wait knowing why.
 *
 * WHAT IT PROMISES, WHICH IS ONLY WHAT IS TRUE. Anything already captured is
 * safe and will send itself; that is the queue's guarantee and this screen may
 * repeat it. It does NOT promise the page will appear when the connection
 * returns, because that depends on a fetch that has not happened yet — it
 * offers a retry instead.
 */

import { useEffect, useState } from 'react';
import { ArrowPathIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { usePendingQueue } from '@/components/Common/usePendingQueue';

export default function OfflineNotice({ onRetry }: { onRetry?: () => void }) {
  const { waiting, blocked } = usePendingQueue();
  const unsent = waiting.length + blocked.length;

  // Retry the moment the connection comes back, rather than making somebody
  // press a button they have no way of knowing has become useful.
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const retry = onRetry ?? (() => window.location.reload());

  return (
    <div
      role="alert"
      className="flex min-h-[50vh] flex-col items-center justify-center px-6 text-center"
    >
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-warning-50 dark:bg-warning-950">
        <SignalSlashIcon className="h-7 w-7 text-warning-700 dark:text-warning-300" />
      </div>

      <h2 className="text-lg font-semibold text-content">
        {online ? 'This page could not be loaded' : 'No connection'}
      </h2>

      <p className="mt-1.5 max-w-md text-sm text-content-muted">
        {online
          ? 'The rest of this page has not been downloaded to this device, and fetching it just failed. Trying again usually fixes it.'
          : 'This part of the app has not been saved to this device, so it cannot open while the connection is down. The rest of the app still works.'}
      </p>

      {/* Only ever claimed when it is true, and only about THIS device — the
          same scope the unsent panel is careful about. */}
      {unsent > 0 && (
        <p className="mt-3 max-w-md text-sm font-medium text-content-secondary">
          {unsent} {unsent === 1 ? 'item' : 'items'} captured on this device
          {unsent === 1 ? ' is' : ' are'} still saved here and will send when the
          connection is back. Nothing has been lost.
        </p>
      )}

      <button onClick={retry} className="btn-primary mt-6 inline-flex items-center gap-2">
        <ArrowPathIcon className="h-4 w-4" />
        Try again
      </button>

      {!online && (
        <p className="mt-3 text-xs text-content-subtle">
          This will start working on its own when the connection returns.
        </p>
      )}
    </div>
  );
}
